import { defineStore } from "pinia";
import { StoreModule } from "@/constants/store";
import api from "@/_helpers/api";
import { axiosInstance } from "@/plugins/https";
import { McpAccessMode } from "@/constants/oauth.ts";

/**
 * Parameters required for the MCP OAuth authorization request.
 * These are parsed from the URL query string on the consent page
 * and forwarded to the backend when the user clicks "Allow".
 */
export interface McpOAuthAuthorizeParams {
  clientId: string;
  redirectUri: string;
  state: string;
  codeChallenge: string;
  codeChallengeMethod: string;
  scope: string;
  accessMode: McpAccessMode;
}

interface OAuthState {
  /** Display name of the MCP agent requesting access (e.g. "Claude") */
  clientName: string;
}

const defaultState: OAuthState = {
  clientName: "",
};

/**
 * OAuth store for MCP agent consent flow.
 *
 * Handles fetching MCP client info, authorizing/denying consent,
 * and redirecting back to the requesting agent.
 * State is intentionally not persisted — consent is a one-shot flow.
 */
export const useOAuthStore = defineStore(StoreModule.OAUTH, {
  state: (): OAuthState => ({ ...defaultState }),
  persist: false,
  actions: {
    /**
     * Fetches the MCP client's display name by client ID.
     * Sets `clientName` in state so the consent page can show
     * which agent is requesting access.
     * @throws If the client ID is unknown or the API request fails.
     */
    async mcpFetchClientInfo(clientId: string) {
      const { data } = await axiosInstance.request<{ clientName: string }>({
        ...api.mcpOAuthClientInfo(clientId),
      });
      this.clientName = data.clientName;
    },

    /**
     * Sends the authorization consent to the backend and redirects
     * the user back to the agent's redirect URI with the authorization code.
     * Performs a hard redirect (window.location.href) since we're leaving the SPA.
     * @throws If the API request fails (component catches this to show an error).
     */
    async mcpAuthorize(params: McpOAuthAuthorizeParams) {
      const { data } = await axiosInstance.request({
        ...api.mcpOAuthAuthorize(),
        data: params,
      });
      const redirectUrl = this.buildRedirectUrl(params.redirectUri, params.state, {
        code: data.code,
      });
      window.location.href = redirectUrl;
    },

    /**
     * Denies the consent request and redirects the user back to the agent's
     * redirect URI with an `error=access_denied` parameter per OAuth 2.0 spec.
     */
    mcpDeny(redirectUri: string, state: string) {
      const redirectUrl = this.buildRedirectUrl(redirectUri, state, {
        error: "access_denied",
      });
      window.location.href = redirectUrl;
    },

    /**
     * Constructs an OAuth redirect URL by appending query parameters
     * to the base redirect URI. Used by both `mcpAuthorize` and `mcpDeny`
     * to build the final redirect URL with either `code` or `error` params.
     * The `state` parameter is only appended when non-empty (per OAuth 2.0 spec).
     */
    buildRedirectUrl(redirectUri: string, state: string, params: Record<string, string>): string {
      const url = new URL(redirectUri);
      for (const [key, value] of Object.entries(params)) {
        url.searchParams.set(key, value);
      }
      if (state) {
        url.searchParams.set("state", state);
      }
      return url.toString();
    },
  },
});
